/**
 * EU Event Routing Configuration
 *
 * Events listed here will route API/WebSocket calls to EU (eu-central-1)
 * endpoints instead of the default ca-central-1 endpoints.
 *
 * To add a new EU event:
 *   1. Add the event identifier (lowercase) to EU_EVENT_IDS
 *   2. Update EU_DEV_ENDPOINTS / EU_PROD_ENDPOINTS if the API Gateway IDs change
 */

export interface EuEndpoints {
  apiUrl: string;
  wsUrl: string;
  xApiKey: string;
}

/**
 * Event identifiers that should route to EU services.
 * Use lowercase hostname prefixes (e.g., 'ata2026' for ata2026.synopsis.rozie.ai)
 */
export const EU_EVENT_IDS: Set<string> = new Set(['wcs2026']);

/** EU Dev (eu-central-1, eu-dev stage) — for dev-* hostname prefixes */
const EU_DEV_ENDPOINTS: EuEndpoints = {
  apiUrl: 'https://eu-dev.api.synopsis.rozie.ai',
  wsUrl: 'wss://ws.eu-dev.api.synopsis.rozie.ai',
  xApiKey: 'Yqa7AKeRCp7ttQxHeeNa32327Hnv9Llw9LtgE8of',
};

/** EU Prod (eu-central-1, eu-prod stage) — for prod hostname prefixes */
const EU_PROD_ENDPOINTS: EuEndpoints = {
  apiUrl: 'https://eu.api.synopsis.rozie.ai',
  wsUrl: 'wss://ws.eu.api.synopsis.rozie.ai',
  xApiKey: 'fQkA6abZqr9WtLBbIziVD4DcVjFzd24g7y7bZbly',
};

/**
 * Check if the current event should use EU endpoints.
 * Extracts event identifier from hostname (e.g., 'ata2026' from 'ata2026.synopsis.rozie.ai')
 */
export function isEuEvent(hostname: string): boolean {
  const eventId = hostname.split('.')[0]?.toLowerCase() ?? '';
  return EU_EVENT_IDS.has(eventId);
}

/**
 * Get EU endpoints for the given hostname, or null if not an EU event.
 * - 'dev-wcs2026' hostname prefix → eu-dev backend
 * - 'wcs2026' hostname prefix     → eu-prod backend
 */
export function getEuEndpointsForHostname(
  hostname: string
): EuEndpoints | null {
  if (!isEuEvent(hostname)) return null;
  const eventId = hostname.split('.')[0]?.toLowerCase() ?? '';
  return eventId.startsWith('dev-') ? EU_DEV_ENDPOINTS : EU_PROD_ENDPOINTS;
}
